import { useEffect, useMemo, useState } from "react";

import { TileJSONResponse, fetchBatchTileJSON } from "api/shapefile";

import { ShapefileFileType } from "models/projects";

import { IEditableProjectLayer } from "components/project/layers/hooks";
import { Shapefile } from "components/project/shapefiles/queries";

interface UseFetchTileJSONParams {
  mapbox: mapboxgl.Map | null;
  shapefileListSorted: Shapefile[];
  layerList: IEditableProjectLayer[];
  enabled?: boolean;
}

/**
 * Hook to batch fetch TileJSON for shapefiles that need to be loaded
 * Separates regular and system shapefiles based on projectLayerType
 */
export function useFetchTileJSON({
  mapbox,
  shapefileListSorted,
  layerList,
  enabled = true
}: UseFetchTileJSONParams) {
  const [tileJSONMap, setTileJSONMap] = useState<Record<string, TileJSONResponse>>({});

  // Determine which shapefiles need TileJSON for initial load
  const shapefilesToFetch = useMemo(() => {
    if (!mapbox) {
      return { regular: [], system: [], key: "" };
    }

    const shapefilesToAdd = shapefileListSorted.filter((shapefile) => {
      const layerName = shapefile.projectShapefileId || shapefile.shapefileId;
      const hasSource = mapbox.getSource(layerName);
      return !hasSource && shapefile.fileType !== ShapefileFileType.GeoTiff;
    });

    const regularShapefileIds: string[] = [];
    const systemShapefileIds: string[] = [];

    // Build map of layerId to projectLayerType for efficient lookup
    const layerTypeMap = new Map<string, string>();
    for (const layer of layerList) {
      const id = layer.projectShapefileId || layer.shapefileId;
      if (id && layer.projectLayerType) {
        layerTypeMap.set(id, layer.projectLayerType);
      }
    }

    // Separate shapefiles into regular and system based on projectLayerType
    for (const shapefile of shapefilesToAdd) {
      const shapefileId = shapefile.shapefileId;
      const layerId = shapefile.projectShapefileId || shapefile.shapefileId;
      const layerType = layerTypeMap.get(layerId);

      if (layerType === "McDaniel") {
        systemShapefileIds.push(shapefileId);
      } else {
        regularShapefileIds.push(shapefileId);
      }
    }

    // Create a unique key to detect if the fetch request has changed
    const key = [...regularShapefileIds, ...systemShapefileIds].sort().join(",");

    return { regular: regularShapefileIds, system: systemShapefileIds, key };
  }, [mapbox, shapefileListSorted, layerList]);

  // Fetch TileJSON for shapefiles that need initial load
  useEffect(() => {
    const { regular, system, key } = shapefilesToFetch;

    // Skip if feature is disabled - return early without fetching
    if (!enabled) {
      setTileJSONMap({});
      return;
    }

    if (!key) {
      return;
    }

    if (regular.length > 0 || system.length > 0) {
      fetchBatchTileJSON(regular, system)
        .then((tileJSONData) => {
          setTileJSONMap((prev) => ({ ...prev, ...tileJSONData }));
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error fetching batch TileJSON:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapefilesToFetch.key, enabled]);

  return tileJSONMap;
}
